import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { AssetsSegmentSchema } from '@iconik-io/iconik-api/src/services/assets';

import { ORDER_TIME_START_MILLISECONDS } from '../../constants/data';
import type { IdsMap } from '../asset_time_based_metadata/types';
import type { AssetCommentsAppState } from './types';

const mergeCommentsRelations = (
  currentRelations: IdsMap,
  newRelations: IdsMap,
) => {
  const resultRelations = { ...currentRelations };
  Object.keys(newRelations).forEach((commentId) => {
    resultRelations[commentId] = Array.from(
      new Set(
        (resultRelations[commentId] || []).concat(newRelations[commentId]),
      ),
    );
  });
  return resultRelations;
};

const initialState: AssetCommentsAppState = {
  segmentIds: {},
  relations: {},
  filterColors: [],
  filterText: '',
  sortField: ORDER_TIME_START_MILLISECONDS,
  loading: {},
};

export const assetCommentsSlice = createSlice({
  name: 'asset_comments',
  initialState,
  reducers: {
    clearAssetComments: () => initialState,

    setAssetCommentsLoading: (
      state,
      action: PayloadAction<{ assetId: string; loading: boolean }>,
    ) => {
      state.loading[action.payload.assetId] = action.payload.loading;
    },

    assetCommentsReceived: (
      state,
      action: PayloadAction<{
        assetId: string;
        segmentIds: string[];
        relations: Record<string, string[]>;
      }>,
    ) => {
      const { assetId, segmentIds, relations } = action.payload;
      state.segmentIds[assetId] = Array.from(
        new Set((state.segmentIds[assetId] || []).concat(segmentIds)),
      );
      state.relations = mergeCommentsRelations(state.relations, relations);
    },

    setColorFilter: (state, action: PayloadAction<{ filters: string[] }>) => {
      state.filterColors = action.payload.filters;
    },

    setTextFilter: (state, action: PayloadAction<{ filters: string }>) => {
      state.filterText = action.payload.filters;
    },

    setSortField: (
      state,
      action: PayloadAction<{
        fieldName: keyof AssetsSegmentSchema | undefined;
      }>,
    ) => {
      state.sortField = action.payload.fieldName;
    },

    setCommentsVersionFilter: (
      state,
      action: PayloadAction<{ versionId: string | undefined }>,
    ) => {
      state.versionFilter = action.payload.versionId;
    },
  },
});

export const {
  clearAssetComments,
  setAssetCommentsLoading,
  assetCommentsReceived,
  setColorFilter,
  setTextFilter,
  setSortField,
  setCommentsVersionFilter,
} = assetCommentsSlice.actions;

export const assetCommentsReducer = assetCommentsSlice.reducer;
